// components/thread-picker-modal.js — THREAD PICKER
// ============================================================================
// Global modal for picking a thread to send items to.
// Loads threads via /api/user/threads (server-side, RLS-safe).
// ============================================================================
import { useState, useEffect } from 'react';

export default function ThreadPickerModal({ isOpen, onClose, onSend, selectedCount, supabase }) {
  const [search, setSearch] = useState('');
  const [threads, setThreads] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setSearch('');
    setSelectedThread(null);
    loadThreads();
  }, [isOpen]);

  async function loadThreads() {
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch('/api/user/threads', {
        headers: { 'Authorization': `Bearer ${session?.access_token || ''}` },
      });
      if (res.ok) {
        const { threads: data } = await res.json();
        setThreads(data || []);
      }
    } catch (e) { console.error('loadThreads:', e); }
    setLoading(false);
  }

  async function handleSend() {
    if (!selectedThread || sending) return;
    setSending(true);
    await onSend(selectedThread);
    setSending(false);
  }

  if (!isOpen) return null;

  const filtered = threads.filter(t =>
    !search || (t.title || 'Untitled').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
      zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        background: 'var(--surface-1)', border: '1px solid var(--border-2)',
        borderRadius: 6, width: '100%', maxWidth: 420, maxHeight: '80vh',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>

        {/* Header */}
        <div style={{
          padding: '14px 16px', borderBottom: '1px solid var(--border-1)',
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
        }}>
          <div>
            <div style={{ fontSize: 13, color: 'var(--text-3)', fontFamily: 'var(--font-serif)', letterSpacing: '0.04em' }}>
              Send to thread
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-6)', fontStyle: 'italic', marginTop: 2 }}>
              {selectedCount} item{selectedCount !== 1 ? 's' : ''} selected — choose a thread
            </div>
          </div>
          <div onClick={onClose} style={{ fontSize: 18, color: 'var(--text-6)', cursor: 'pointer', lineHeight: 1 }}>×</div>
        </div>

        {/* Search */}
        <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border-1)' }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            background: 'var(--surface-4)', border: '1px solid var(--border-2)',
            borderRadius: 'var(--radius-md)', padding: '7px 10px',
          }}>
            <svg width="12" height="12" viewBox="0 0 14 14" fill="none" stroke="var(--text-5)" strokeWidth="1.5">
              <circle cx="5.5" cy="5.5" r="4"/><path d="M9 9l3 3"/>
            </svg>
            <input
              type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search threads..." autoFocus
              style={{ background: 'none', border: 'none', outline: 'none', color: 'var(--text-2)', fontSize: 12, fontFamily: 'var(--font-serif)', width: '100%' }}
            />
          </div>
        </div>

        {/* Thread list */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {loading ? (
            <div style={{ padding: 24, textAlign: 'center', fontSize: 12, color: 'var(--text-6)', fontStyle: 'italic' }}>Loading threads...</div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', fontSize: 12, color: 'var(--text-6)', fontStyle: 'italic' }}>No threads found</div>
          ) : (
            filtered.map(t => {
              const isChosen = selectedThread?.id === t.id;
              const date = new Date(t.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
              return (
                <div key={t.id} onClick={() => setSelectedThread(isChosen ? null : t)} style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '9px 14px', cursor: 'pointer',
                  borderLeft: `2px solid ${isChosen ? '#c8a96e' : 'transparent'}`,
                  background: isChosen ? 'rgba(200,169,110,0.04)' : 'transparent', transition: 'all 0.15s',
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12, color: 'var(--text-3)', lineHeight: 1.3 }}>{t.title || 'Untitled thread'}</div>
                    <div style={{ fontSize: 10, color: 'var(--text-6)', marginTop: 2, fontStyle: 'italic' }}>{date}</div>
                  </div>
                  <div style={{
                    width: 14, height: 14, borderRadius: 3,
                    border: `1px solid ${isChosen ? '#c8a96e' : 'var(--border-2)'}`,
                    background: isChosen ? '#c8a96e' : 'var(--surface-4)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    {isChosen && <svg width="9" height="9" viewBox="0 0 9 9" fill="none" stroke="#fff" strokeWidth="2"><path d="M1.5 4.5l2.5 2.5L7.5 2"/></svg>}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '10px 14px', borderTop: '1px solid var(--border-1)',
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <div style={{ flex: 1, fontSize: 11, color: selectedThread ? 'var(--text-5)' : 'var(--text-6)', fontStyle: 'italic', fontFamily: 'var(--font-serif)' }}>
            {selectedThread ? `→ ${(selectedThread.title || 'Untitled').substring(0, 35)}` : 'No thread selected'}
          </div>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', color: 'var(--text-6)',
            cursor: 'pointer', fontSize: 11, fontFamily: 'var(--font-serif)',
          }}>Cancel</button>
          <button onClick={handleSend} disabled={!selectedThread || sending} style={{
            background: 'transparent',
            border: `1px solid ${selectedThread ? 'rgba(122,158,130,0.5)' : 'var(--border-2)'}`,
            color: selectedThread ? 'var(--sage)' : 'var(--text-6)',
            fontSize: 11, fontFamily: 'var(--font-serif)', padding: '5px 14px',
            borderRadius: 3, cursor: selectedThread ? 'pointer' : 'default', transition: 'all 0.15s',
          }}>{sending ? 'Sending...' : 'Send →'}</button>
        </div>
      </div>
    </div>
  );
}
